import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import { ILabelsState } from '../../../services/planogram-activity.service';
import { SessionService } from '../../../../core/services/session.service';
import { PanelHeaderTitle, SidePanelService } from '../../../../core/services/side-panel.service';
import { FormControl, FormGroup, NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
	sessionDateLimits,
	toApiDateRange,
	toDateRangeString
} from '../../../../core/utils/ph-datepicker.utils';

@Component({
    selector: 'app-labels-configure-panel',
    templateUrl: './labels-configure-panel.component.html',
    styleUrls: ['./labels-configure-panel.component.scss'],
    standalone: false
})
export class LabelsConfigurePanelComponent implements OnInit, OnDestroy {
	allShops: boolean = false;
	formGroup: FormGroup;
	isSomeLabelActive: boolean = false;
	subscriptions: Array<Subscription> = [];
	selectedDateRange: DateRange<string>;
	dateLimits = sessionDateLimits(null);

	@Input('stateVersion') stateVersion: ILabelsState;
	@Input('isLibrary') isLibrary: boolean;
	@Input('labelsDate') defaultDate: any;

	@Output('apply') apply: EventEmitter<any> = new EventEmitter<any>();

	@ViewChild('form') protected formElement: NgForm;

	constructor(
		private session: SessionService,
		private panelService: SidePanelService
	) {}

	ngOnInit(): void {
		this.panelService.title = PanelHeaderTitle.ProductLabels;
		this.panelService.hasBackdrop = true;
		this.dateLimits = sessionDateLimits(this.session.defaults);
		this.subscriptions.push(
			this.panelService.applySubject.subscribe(() => {
				this.formElement.onSubmit(this.formGroup.value);
			}),
			this.panelService.cancelSubject.subscribe(() => {
				const value: Partial<ILabelsState> = {
					abcReceipts: false,
					abcTurnover: false,
					allShops: false,
					price: false,
					stockQty: false,
					dateFrom: this.defaultDate.date_from,
					dateTo: this.defaultDate.date_to
				};
				this.apply.emit(value);
			})
		);

		this.setFormGroup();
	}

	setFormGroup(): void {
		this.formGroup = new FormGroup({
			price: new FormControl(this.stateVersion.price),
			stockQty: new FormControl(this.stateVersion.stockQty),
			abcTurnover: new FormControl(this.stateVersion.abcTurnover),
			abcReceipts: new FormControl(this.stateVersion.abcReceipts),
			allShops: new FormControl(this.stateVersion.allShops ? 1 : 0),
			dateFrom: new FormControl(this.stateVersion.dateFrom),
			dateTo: new FormControl(this.stateVersion.dateTo)
		});

		this.selectedDateRange = toDateRangeString(this.stateVersion.dateFrom, this.stateVersion.dateTo);

		this.isSomeLabelActive = this.stateVersion.abcReceipts || this.stateVersion.abcTurnover;
		this.setAllShopsDisabled();

		this.panelService.disabledApply = true;
		this.formGroup.valueChanges.subscribe((values: ILabelsState) => {
			this.panelService.disabledApply = this.formElement.submitted && this.formElement.invalid;
			this.isSomeLabelActive = values.abcReceipts || values.abcTurnover;
			this.setAllShopsDisabled();
		});
	}

	setAllShopsDisabled(): void {
		if (this.isSomeLabelActive && this.isLibrary) {
			this.formGroup.get('allShops').enable({ emitEvent: false });
		} else {
			this.formGroup.get('allShops').disable({ emitEvent: false });
		}
	}

	applyPanel(): void {
		this.panelService.disabledApply = this.formElement.submitted && this.formElement.invalid;
		if (this.formElement.invalid) {
			return;
		}

		const value: ILabelsState = this.formGroup.getRawValue();
		value.allShops = Boolean(value.allShops);
		this.apply.emit(value);
	}

	dateHandler(data: Date | DateRange<Date> | null): void {
		const range = toApiDateRange(data);
		if (!range) return;
		this.selectedDateRange = toDateRangeString(range.date_from, range.date_to);
		this.formGroup.get('dateFrom').setValue(range.date_from);
		this.formGroup.get('dateTo').setValue(range.date_to);
	}

	ngOnDestroy() {
		this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
	}
}
